import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Custom Chemical (organization-specific)
 */
export interface CustomChemical {
  id: string;
  organizationId: string;
  name: string;
  epaNumber?: string;
  manufacturer?: string;
  activeIngredient?: string;
  signalWord?: 'CAUTION' | 'WARNING' | 'DANGER';
  category?: 'herbicide' | 'insecticide' | 'fungicide' | 'fertilizer' | 'other';
  formulation?: string;
  rei?: string;
  phi?: string;
  defaultRate?: string;
  targetPests?: string[];
  crops?: string[];
  notes?: string;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * DTO for creating custom chemical
 */
export interface CreateCustomChemicalDto {
  name: string;
  epaNumber?: string;
  manufacturer?: string;
  activeIngredient?: string;
  signalWord?: 'CAUTION' | 'WARNING' | 'DANGER';
  category?: 'herbicide' | 'insecticide' | 'fungicide' | 'fertilizer' | 'other';
  formulation?: string;
  rei?: string;
  phi?: string;
  defaultRate?: string;
  targetPests?: string[];
  crops?: string[];
  notes?: string;
}

/**
 * DTO for updating custom chemical
 */
export interface UpdateCustomChemicalDto extends Partial<CreateCustomChemicalDto> {}

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Custom Chemical Service
 *
 * Manages organization-specific custom chemicals.
 * Requires X-Organization-Id header (handled by AuthInterceptor).
 *
 * Reference: /agent-os/specs/2026-01-07-chemical-templates-backend/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class CustomChemicalService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // Signal-based state management
  private chemicalsSignal = signal<CustomChemical[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals
  readonly chemicals = computed(() => this.chemicalsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  /**
   * Load custom chemicals for current organization
   * GET /custom-chemicals?search=&category=
   */
  async loadChemicals(options: {
    search?: string;
    category?: string;
  } = {}): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params: any = {};
      if (options.search) params.search = options.search;
      if (options.category) params.category = options.category;

      const response = await firstValueFrom(
        this.http.get<ApiResponse<CustomChemical[]>>(
          `${this.apiUrl}/custom-chemicals`,
          { params }
        )
      );

      if (response.success && response.data) {
        this.chemicalsSignal.set(response.data);
      } else {
        throw new Error(response.error || 'Failed to load custom chemicals');
      }
    } catch (error: any) {
      console.error('Error loading custom chemicals:', error);
      this.errorSignal.set(error.message || 'Failed to load custom chemicals');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get custom chemical by ID
   * GET /custom-chemicals/:id
   */
  async getChemicalById(id: string): Promise<CustomChemical | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<CustomChemical>>(
          `${this.apiUrl}/custom-chemicals/${id}`
        )
      );

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    } catch (error: any) {
      console.error('Error fetching custom chemical:', error);
      this.errorSignal.set(error.message || 'Failed to fetch custom chemical');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Create a new custom chemical
   * POST /custom-chemicals
   */
  async createChemical(dto: CreateCustomChemicalDto): Promise<CustomChemical | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<CustomChemical>>(
          `${this.apiUrl}/custom-chemicals`,
          dto
        )
      );

      if (response.success && response.data) {
        // Add to local state
        this.chemicalsSignal.update(chems => [...chems, response.data!]);
        return response.data;
      }

      throw new Error(response.error || 'Failed to create custom chemical');
    } catch (error: any) {
      console.error('Error creating custom chemical:', error);
      this.errorSignal.set(error.message || 'Failed to create custom chemical');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update an existing custom chemical
   * PUT /custom-chemicals/:id
   */
  async updateChemical(
    id: string,
    dto: UpdateCustomChemicalDto
  ): Promise<CustomChemical | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<CustomChemical>>(
          `${this.apiUrl}/custom-chemicals/${id}`,
          dto
        )
      );

      if (response.success && response.data) {
        // Update local state
        this.chemicalsSignal.update(chems => {
          const index = chems.findIndex(c => c.id === id);
          if (index !== -1) {
            const updated = [...chems];
            updated[index] = response.data!;
            return updated;
          }
          return chems;
        });

        return response.data;
      }

      throw new Error(response.error || 'Failed to update custom chemical');
    } catch (error: any) {
      console.error('Error updating custom chemical:', error);
      this.errorSignal.set(error.message || 'Failed to update custom chemical');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Delete a custom chemical (soft-delete)
   * DELETE /custom-chemicals/:id
   */
  async deleteChemical(id: string): Promise<boolean> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(
          `${this.apiUrl}/custom-chemicals/${id}`
        )
      );

      if (response.success) {
        // Remove from local state
        this.chemicalsSignal.update(chems => chems.filter(c => c.id !== id));
        return true;
      }

      throw new Error(response.error || 'Failed to delete custom chemical');
    } catch (error: any) {
      console.error('Error deleting custom chemical:', error);
      this.errorSignal.set(error.message || 'Failed to delete custom chemical');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Search custom chemicals (client-side filtering)
   */
  searchChemicals(query: string): CustomChemical[] {
    if (!query || query.trim().length < 2) {
      return this.chemicalsSignal();
    }

    const searchTerm = query.toLowerCase().trim();
    return this.chemicalsSignal().filter(c =>
      c.name.toLowerCase().includes(searchTerm) ||
      c.epaNumber?.toLowerCase().includes(searchTerm) ||
      c.activeIngredient?.toLowerCase().includes(searchTerm) ||
      c.manufacturer?.toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Get chemicals by category (client-side filtering)
   */
  getChemicalsByCategory(category: string): CustomChemical[] {
    return this.chemicalsSignal().filter(c => c.category === category);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
